import { atom, useAtom } from 'jotai';
import { useCallback } from 'react';
import { ToggleType } from '../types';
export const selectedItemAtom = atom({
  selectedGroup: undefined,
  selectedRule: undefined
});
export const useCollapseItems = () => {
  const [{
    selectedGroup
  }, setSelectedItem] = useAtom(selectedItemAtom);
  const collapseGroup = useCallback(() => {
    setSelectedItem({
      selectedGroup: null,
      selectedRule: null
    });
  }, [setSelectedItem]);
  const collapseRule = useCallback(() => {
    setSelectedItem({
      selectedGroup,
      selectedRule: null
    });
  }, [selectedGroup, setSelectedItem]);
  return {
    collapseGroup,
    collapseRule
  };
};
export const useSelectedItemState = () => {
  const [{
    selectedGroup,
    selectedRule
  }, setSelectedItem] = useAtom(selectedItemAtom);
  return {
    selectedGroup,
    selectedRule,
    setSelectedItem
  };
};
export const useOnRuleGroupToggle = _ref => {
  let {
    unsavedChangesHandlers,
    store
  } = _ref;
  const [{
    selectedGroup,
    selectedRule
  }, setSelectedItem] = useAtom(selectedItemAtom, {
    store
  });
  const onToggleRule = useCallback((ruleId, ruleResults) => {
    const selectedRuleIndex = ruleResults.findIndex(ruleResult => ruleResult.id === (selectedRule == null ? void 0 : selectedRule.id)) ?? -1;
    if (selectedRule === null || selectedRule === undefined || selectedRule.id !== ruleId) {
      var _unsavedChangesHandle;
      selectedRule == null || selectedRule.onRuleToggle == null || selectedRule.onRuleToggle(ToggleType.Collapse, selectedRuleIndex, false);
      const newSelectedRule = ruleResults.find(ruleResult => ruleResult.id === ruleId) ?? null;
      const newSelectedRuleIndex = ruleResults.findIndex(ruleResult => ruleResult.id === ruleId) ?? -1;
      const expandNewRuleActions = () => {
        setSelectedItem({
          selectedGroup,
          selectedRule: newSelectedRule
        });
        newSelectedRule == null || newSelectedRule.onRuleToggle == null || newSelectedRule.onRuleToggle(ToggleType.Expand, newSelectedRuleIndex, true);
      };
      if ((selectedRule == null ? void 0 : selectedRule.id) !== ruleId && unsavedChangesHandlers != null && (_unsavedChangesHandle = unsavedChangesHandlers.navigationGuard) != null && _unsavedChangesHandle.exists) {
        unsavedChangesHandlers.navigationGuard.onNavigationAttempt(() => {
          expandNewRuleActions();
        });
      } else if ((selectedRule == null ? void 0 : selectedRule.id) !== ruleId && unsavedChangesHandlers != null && unsavedChangesHandlers.unsavedChanges.exists) {
        const {
          unsavedChanges,
          showSaveBeforeLeaveModal,
          setUnsavedChanges
        } = unsavedChangesHandlers;
        showSaveBeforeLeaveModal(unsavedChanges.apply, () => {
          unsavedChanges.discard == null || unsavedChanges.discard();
          expandNewRuleActions();
          setUnsavedChanges(false, () => Promise.resolve());
        });
      } else {
        expandNewRuleActions();
      }
    } else {
      var _unsavedChangesHandle2;
      const collapseRuleAction = () => {
        selectedRule == null || selectedRule.onRuleToggle == null || selectedRule.onRuleToggle(ToggleType.Collapse, selectedRuleIndex, true);
        setSelectedItem({
          selectedGroup,
          selectedRule: null
        });
      };
      if (unsavedChangesHandlers != null && (_unsavedChangesHandle2 = unsavedChangesHandlers.navigationGuard) != null && _unsavedChangesHandle2.exists) {
        unsavedChangesHandlers.navigationGuard.onNavigationAttempt(() => {
          collapseRuleAction();
        });
      } else if (unsavedChangesHandlers != null && unsavedChangesHandlers.unsavedChanges.exists) {
        unsavedChangesHandlers.showSaveBeforeLeaveModal(unsavedChangesHandlers.unsavedChanges.apply, () => {
          unsavedChangesHandlers.unsavedChanges.discard == null || unsavedChangesHandlers.unsavedChanges.discard();
          collapseRuleAction();
          unsavedChangesHandlers.setUnsavedChanges(false, async () => {});
        });
      } else {
        collapseRuleAction();
      }
    }
  }, [selectedGroup, selectedRule, setSelectedItem, unsavedChangesHandlers]);
  const isRuleOpen = useCallback(ruleResult => {
    return ruleResult.id === (selectedRule == null ? void 0 : selectedRule.id);
  }, [selectedRule]);
  const onGroupToggle = useCallback((displayedRuleGroups, groupId) => {
    const selectedGroupIndex = (displayedRuleGroups == null ? void 0 : displayedRuleGroups.findIndex(ruleGroup => ruleGroup.groupId === (selectedGroup == null ? void 0 : selectedGroup.groupId))) ?? -1;
    if (selectedGroup === null || selectedGroup === undefined || selectedGroup.groupId !== groupId) {
      var _unsavedChangesHandle3;
      selectedGroup == null || selectedGroup.onGroupToggle == null || selectedGroup.onGroupToggle(ToggleType.Collapse, selectedGroupIndex, false);
      const newSelectedGroup = (displayedRuleGroups == null ? void 0 : displayedRuleGroups.find(ruleGroup => ruleGroup.groupId === groupId)) ?? null;
      const newSelectedGroupIndex = (displayedRuleGroups == null ? void 0 : displayedRuleGroups.findIndex(group => group.groupId === groupId)) ?? -1;
      const expandGroupAction = () => {
        newSelectedGroup == null || newSelectedGroup.onGroupToggle == null || newSelectedGroup.onGroupToggle(ToggleType.Expand, newSelectedGroupIndex, true);
        setSelectedItem({
          selectedGroup: newSelectedGroup,
          selectedRule: null
        });
      };
      if ((selectedGroup == null ? void 0 : selectedGroup.groupId) !== groupId && unsavedChangesHandlers != null && (_unsavedChangesHandle3 = unsavedChangesHandlers.navigationGuard) != null && _unsavedChangesHandle3.exists) {
        unsavedChangesHandlers.navigationGuard.onNavigationAttempt(() => {
          expandGroupAction();
        });
      } else if ((selectedGroup == null ? void 0 : selectedGroup.groupId) !== groupId && unsavedChangesHandlers != null && unsavedChangesHandlers.unsavedChanges.exists) {
        const {
          unsavedChanges,
          showSaveBeforeLeaveModal,
          setUnsavedChanges
        } = unsavedChangesHandlers;
        showSaveBeforeLeaveModal(unsavedChanges.apply, () => {
          unsavedChanges.discard == null || unsavedChanges.discard();
          newSelectedGroup == null || newSelectedGroup.onGroupToggle == null || newSelectedGroup.onGroupToggle(ToggleType.Expand, newSelectedGroupIndex, true);
          expandGroupAction();
          setUnsavedChanges(false, () => Promise.resolve());
        });
      } else {
        expandGroupAction();
      }
    } else {
      var _unsavedChangesHandle4;
      const collapseGroupActions = () => {
        selectedGroup == null || selectedGroup.onGroupToggle == null || selectedGroup.onGroupToggle(ToggleType.Collapse, selectedGroupIndex, true);
        setSelectedItem({
          selectedGroup: null,
          selectedRule: null
        });
      };
      if (unsavedChangesHandlers != null && (_unsavedChangesHandle4 = unsavedChangesHandlers.navigationGuard) != null && _unsavedChangesHandle4.exists) {
        unsavedChangesHandlers.navigationGuard.onNavigationAttempt(() => {
          collapseGroupActions();
        });
      } else if (unsavedChangesHandlers != null && unsavedChangesHandlers.unsavedChanges.exists) {
        const {
          unsavedChanges,
          showSaveBeforeLeaveModal,
          setUnsavedChanges
        } = unsavedChangesHandlers;
        showSaveBeforeLeaveModal(unsavedChanges.apply, () => {
          unsavedChanges.discard == null || unsavedChanges.discard();
          selectedGroup == null || selectedGroup.onGroupToggle == null || selectedGroup.onGroupToggle(ToggleType.Collapse, selectedGroupIndex, true);
          collapseGroupActions();
          setUnsavedChanges(false, () => Promise.resolve());
        });
      } else {
        collapseGroupActions();
      }
    }
  }, [selectedGroup, setSelectedItem, unsavedChangesHandlers]);
  const isGroupOpen = useCallback((ruleGroupCandidate, initiallyOpenedGroup) => {
    if (selectedGroup === undefined && selectedRule === undefined && (initiallyOpenedGroup == null ? void 0 : initiallyOpenedGroup.groupId) === ruleGroupCandidate.groupId) {
      setSelectedItem({
        selectedGroup: ruleGroupCandidate,
        selectedRule: undefined
      });
      return true;
    }
    return (selectedGroup == null ? void 0 : selectedGroup.groupId) === ruleGroupCandidate.groupId;
  }, [selectedGroup, selectedRule, setSelectedItem]);
  return {
    isGroupOpen,
    isRuleOpen,
    onGroupToggle,
    onToggleRule,
    selectedGroup,
    selectedRule,
    setSelectedItem
  };
};
//# sourceMappingURL=use-on-rule-group-toggle.js.map